import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatINR, productImg } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Tag, X, Check, CreditCard, Banknote, Phone } from "lucide-react";
import { track } from "@/lib/track";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [couponApplying, setCouponApplying] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [payments, setPayments] = useState({ cod_enabled: true, cod_fee: 5000, cod_otp_required: false });
  const [method, setMethod] = useState("razorpay");
  const [addr, setAddr] = useState({
    full_name: user?.name || "", phone: "", line1: "", line2: "",
    city: "", state: "", pincode: "", country: "India",
  });

  // Phone OTP state (only used for COD)
  const [phoneOtpToken, setPhoneOtpToken] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [phoneCode, setPhoneCode] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  // Pincode → City/State auto-fill (India Post)
  const [pinLookup, setPinLookup] = useState({ loading: false, ok: false, error: "", state: "", city: "" });

  useEffect(() => {
    api.get("/content").then(({ data }) => {
      if (data?.payments) setPayments(data.payments);
    }).catch(() => {});
  }, []);

  // Fire InitiateCheckout the first time the checkout page mounts with items
  const [initFired, setInitFired] = useState(false);
  useEffect(() => {
    if (initFired || items.length === 0) return;
    const t = subtotal;
    track("InitiateCheckout", { total: t, items });
    setInitFired(true);
  }, [items, subtotal, initFired]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  // If the phone number changes, invalidate any existing OTP token
  useEffect(() => {
    setPhoneOtpToken(null); setOtpSent(false); setPhoneCode("");
  }, [addr.phone]);

  // Pincode → city/state auto-lookup (debounced)
  useEffect(() => {
    const pin = (addr.pincode || "").replace(/\D/g, "");
    if (pin.length !== 6) {
      setPinLookup({ loading: false, ok: false, error: "", state: "", city: "" });
      return;
    }
    let cancelled = false;
    setPinLookup((s) => ({ ...s, loading: true, ok: false, error: "" }));
    const controller = new AbortController();
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`, { signal: controller.signal });
        const json = await res.json();
        const row = Array.isArray(json) ? json[0] : null;
        const po = row?.PostOffice?.[0];
        if (cancelled) return;
        if (row?.Status === "Success" && po) {
          const city = po.District || po.Block || po.Name || "";
          const state = po.State || "";
          setPinLookup({ loading: false, ok: true, error: "", city, state });
          // Auto-fill only if user hasn't already typed something manually
          setAddr((a) => ({
            ...a,
            city: a.city ? a.city : city,
            state: a.state ? a.state : state,
            pincode: pin,
          }));
        } else {
          setPinLookup({ loading: false, ok: false, error: "Not a valid Indian pincode", state: "", city: "" });
        }
      } catch (err) {
        if (cancelled || err.name === "AbortError") return;
        setPinLookup({ loading: false, ok: false, error: "Couldn't look up pincode. Try again.", state: "", city: "" });
      }
    }, 400);
    return () => { cancelled = true; controller.abort(); clearTimeout(t); };
  }, [addr.pincode]);

  const discount = coupon?.discount || 0;
  const shipping = subtotal - discount >= 69900 ? 0 : 5900;
  const codCharge = method === "cod" ? (payments.cod_fee || 5000) : 0;
  const total = Math.max(0, subtotal - discount + shipping + codCharge);

  // If the account already has a verified phone matching the entered delivery
  // phone, we can skip OTP for COD.
  const accountVerified = user?.phone_verified && user?.phone
    && normalizeIndianPhone(user.phone) === normalizeIndianPhone(addr.phone);
  const needsPhoneOtp = method === "cod" && payments.cod_otp_required && !phoneOtpToken && !accountVerified;

  const onChange = (k) => (e) => setAddr({ ...addr, [k]: e.target.value });

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponApplying(true);
    try {
      const { data } = await api.post("/coupons/apply", { code: couponInput.trim(), subtotal });
      setCoupon(data); toast.success(`Code "${data.code}" applied — ${formatINR(data.discount)} off`);
    } catch (e) { setCoupon(null); toast.error(e.response?.data?.detail || "Invalid coupon"); }
    finally { setCouponApplying(false); }
  };
  const removeCoupon = () => { setCoupon(null); setCouponInput(""); };

  const loadRzp = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const sendPhoneOtp = async () => {
    if (!addr.phone || addr.phone.replace(/\D/g, "").length < 10) {
      return toast.error("Enter a valid 10-digit mobile number");
    }
    setOtpBusy(true);
    try {
      await api.post("/auth/send-phone-otp", { phone: addr.phone });
      toast.success("Code sent to your phone ♡");
      setOtpSent(true); setResendIn(30);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send code");
    } finally { setOtpBusy(false); }
  };

  const verifyPhoneOtp = async () => {
    if (phoneCode.length !== 6) return toast.error("Enter the 6-digit code");
    setOtpBusy(true);
    try {
      const { data } = await api.post("/auth/verify-phone-otp", { phone: addr.phone, code: phoneCode });
      setPhoneOtpToken(data.phone_otp_token);
      toast.success("Phone verified ✓");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not verify");
    } finally { setOtpBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error("Your bag is empty"); return; }
    for (const k of ["full_name","phone","line1","city","state","pincode"]) {
      if (!addr[k]) { toast.error("Please fill all shipping fields"); return; }
    }
    if (!pinLookup.ok || addr.pincode.length !== 6) {
      toast.error("Please enter a valid Indian PIN code so we can set your city & state");
      return;
    }
    // Manual override allowed — but state must match the pincode's state
    if (pinLookup.state && addr.state.trim().toLowerCase() !== pinLookup.state.toLowerCase()) {
      toast.error(`State doesn't match your PIN. ${addr.pincode} is in ${pinLookup.state}.`);
      return;
    }
    if (method === "cod" && needsPhoneOtp) {
      toast.error("Please verify your mobile number to place a COD order");
      return;
    }
    setProcessing(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        address: addr,
        coupon_code: coupon?.code || null,
        payment_method: method,
        phone_otp_token: method === "cod" ? phoneOtpToken : null,
      });
      const order = data.order;

      if (method === "cod") {
        track("Purchase", {
          order_id: order.id, total: order.total, items: order.items,
          payment_method: "cod", coupon: coupon?.code,
        });
        clear();
        toast.success("Order placed! Pay on delivery ♡");
        navigate(`/account?order=${order.id}`);
        return;
      }

      if (!data.gateway_ready || !data.razorpay_key_id || data.razorpay_key_id.includes("PLACEHOLDER")) {
        toast.error("Payments are not configured yet. Please try again shortly.");
        setProcessing(false);
        return;
      }
      const ok = await loadRzp();
      if (!ok) { toast.error("Could not load Razorpay"); setProcessing(false); return; }
      const rzp = new window.Razorpay({
        key: data.razorpay_key_id, amount: order.total, currency: order.currency,
        name: "Nailedit Beauty", description: "Nail sticker order", order_id: data.razorpay_order_id,
        prefill: { name: addr.full_name, contact: addr.phone, email: user?.email || "" },
        theme: { color: "#ED6A92" },
        handler: async (resp) => {
          try {
            await api.post("/orders/verify", {
              order_id: order.id, razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature,
            });
            track("Purchase", {
              order_id: order.id, total: order.total, items: order.items,
              payment_method: "razorpay", coupon: coupon?.code,
            });
            clear(); toast.success("Payment successful ♡");
            navigate(`/account?order=${order.id}`);
          } catch { toast.error("Verification failed"); }
        },
        modal: { ondismiss: () => setProcessing(false) },
      });
      rzp.open();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not place order"); }
    finally { setProcessing(false); }
  };

  return (
    <div data-testid="checkout-page" className="min-h-screen pb-20">
      <div className="max-w-[1180px] mx-auto px-6 pt-12">
        <span className="eyebrow">Almost yours</span>
        <h1 className="text-[clamp(2.4rem,5vw,3.5rem)] mt-3 mb-2">Checkout</h1>
        <p className="text-[var(--ink-soft)]">Pretty nails are on the way.</p>

        <form onSubmit={submit} className="mt-10 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="surface p-8 space-y-5">
              <span className="eyebrow">Shipping</span>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name" testid="addr-name" value={addr.full_name} onChange={onChange("full_name")} />
                <Field label="Phone" testid="addr-phone" value={addr.phone} onChange={onChange("phone")} />
                <Field label="Address line 1" testid="addr-line1" value={addr.line1} onChange={onChange("line1")} full />
                <Field label="Address line 2 (optional)" testid="addr-line2" value={addr.line2} onChange={onChange("line2")} full />
                {/* Pincode first — city/state auto-fill from it */}
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.16em] text-[var(--ink-soft)] font-bold flex items-center gap-2">
                    PIN code
                    {pinLookup.loading && <span className="text-[10px] normal-case tracking-normal text-[var(--ink-soft)]">Looking up…</span>}
                    {pinLookup.ok && <span className="text-[10px] normal-case tracking-normal text-green-700 flex items-center gap-1"><Check className="w-3 h-3"/> Found</span>}
                    {pinLookup.error && <span className="text-[10px] normal-case tracking-normal text-red-600">{pinLookup.error}</span>}
                  </span>
                  <input
                    data-testid="addr-pincode"
                    inputMode="numeric"
                    maxLength={6}
                    value={addr.pincode}
                    onChange={(e) => setAddr({ ...addr, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                    placeholder="6-digit PIN"
                    className="mt-1.5 w-full bg-white border border-[#fde0e8] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--bloom)]"
                  />
                </label>
                <Field label="City" testid="addr-city" value={addr.city} onChange={onChange("city")} />
                <Field label="State" testid="addr-state" value={addr.state} onChange={onChange("state")} />
                <Field label="Country" testid="addr-country" value={addr.country} onChange={onChange("country")} full />
              </div>
            </div>

            {/* Payment method */}
            <div className="surface p-8 space-y-5">
              <span className="eyebrow">Payment method</span>
              <div className="grid sm:grid-cols-2 gap-3">
                <MethodCard
                  active={method === "razorpay"}
                  onClick={() => setMethod("razorpay")}
                  icon={<CreditCard className="w-5 h-5" />}
                  title="Pay Online"
                  subtitle="UPI · Cards · Netbanking · Wallets"
                  testid="pm-razorpay"
                />
                {payments.cod_enabled && (
                  <MethodCard
                    active={method === "cod"}
                    onClick={() => setMethod("cod")}
                    icon={<Banknote className="w-5 h-5" />}
                    title="Cash on Delivery"
                    subtitle={payments.cod_otp_required
                      ? `+${formatINR(payments.cod_fee || 5000)} handling fee · Phone OTP required`
                      : `+${formatINR(payments.cod_fee || 5000)} handling fee · Pay in cash on delivery`}
                    testid="pm-cod"
                  />
                )}
              </div>

              {method === "cod" && payments.cod_otp_required && !accountVerified && (
                <div data-testid="cod-otp-panel" className="mt-2 rounded-2xl border border-[#fde0e8] bg-white p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Phone className="w-4 h-4" style={{ color: "var(--bloom)" }} />
                    <div className="font-serif font-semibold">Verify your mobile</div>
                    {phoneOtpToken && (
                      <span className="ml-auto text-xs font-bold uppercase tracking-wider text-green-700 flex items-center gap-1">
                        <Check className="w-3 h-3" /> Verified
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[var(--ink-soft)] mb-3">
                    We send a 6-digit code to <b style={{ color: "var(--ink)" }}>{addr.phone || "your mobile"}</b>. Required so we can confirm your COD order.
                  </p>
                  {!otpSent ? (
                    <button
                      type="button"
                      onClick={sendPhoneOtp}
                      disabled={otpBusy || !addr.phone}
                      data-testid="cod-send-otp"
                      className="btn btn-primary text-sm"
                    >
                      {otpBusy ? "Sending…" : "Send OTP"}
                    </button>
                  ) : phoneOtpToken ? (
                    <button
                      type="button"
                      onClick={() => { setPhoneOtpToken(null); setOtpSent(false); setPhoneCode(""); }}
                      data-testid="cod-change-phone"
                      className="text-xs uppercase tracking-[0.16em] font-bold text-[var(--ink-soft)] hover:text-[var(--bloom)]"
                    >
                      Change number
                    </button>
                  ) : (
                    <div className="flex flex-wrap items-center gap-3">
                      <input
                        data-testid="cod-otp-code"
                        inputMode="numeric"
                        maxLength={6}
                        value={phoneCode}
                        onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="6-digit code"
                        className="w-40 bg-white border border-[#fde0e8] rounded-2xl px-4 py-3 text-center font-serif tracking-[0.4em] text-lg focus:outline-none focus:border-[var(--bloom)]"
                      />
                      <button
                        type="button"
                        onClick={verifyPhoneOtp}
                        disabled={otpBusy || phoneCode.length !== 6}
                        data-testid="cod-verify-otp"
                        className="btn btn-primary text-sm disabled:opacity-60"
                      >
                        {otpBusy ? "Verifying…" : "Verify"}
                      </button>
                      <button
                        type="button"
                        onClick={sendPhoneOtp}
                        disabled={resendIn > 0 || otpBusy}
                        data-testid="cod-resend-otp"
                        className="text-xs uppercase tracking-[0.14em] font-bold disabled:opacity-40"
                        style={{ color: "var(--bloom)" }}
                      >
                        {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="surface p-6 sticky top-24">
              <span className="eyebrow">Order summary</span>
              <div className="space-y-3 max-h-60 overflow-y-auto pr-1 mt-4">
                {items.map((i) => (
                  <div key={i.product_id} className="flex gap-3">
                    <img src={productImg(i.image)} alt={i.name} className="w-14 h-14 rounded-xl object-cover" />
                    <div className="flex-1 text-sm">
                      <div className="font-serif font-semibold leading-tight">{i.name}</div>
                      <div className="text-[var(--ink-soft)] text-xs">Qty {i.qty}</div>
                    </div>
                    <div className="text-sm font-bold" style={{color:"var(--bloom)"}}>{formatINR(i.price * i.qty)}</div>
                  </div>
                ))}
              </div>

              <div className="mt-5 pt-4 border-t border-[#fde0e8]">
                {!coupon ? (
                  <>
                    <label className="text-xs uppercase tracking-[0.18em] text-[var(--ink-soft)] font-bold flex items-center gap-1.5">
                      <Tag className="w-3 h-3"/> Promo code
                    </label>
                    <div className="mt-2 flex gap-2">
                      <input data-testid="coupon-input" value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                        placeholder="GLITTER10"
                        className="flex-1 bg-white border border-[#fde0e8] rounded-2xl px-3 py-2.5 text-sm uppercase tracking-wider focus:outline-none focus:border-[var(--bloom)]"/>
                      <button type="button" onClick={applyCoupon} disabled={couponApplying || !couponInput.trim()}
                        data-testid="apply-coupon-btn" className="btn btn-primary text-xs">
                        {couponApplying ? "..." : "Apply"}
                      </button>
                    </div>
                    <p className="mt-1.5 text-[10px] text-[var(--ink-soft)] uppercase tracking-wider">
                      Try <span className="font-bold" style={{color:"var(--bloom)"}}>GLITTER10</span>
                    </p>
                  </>
                ) : (
                  <div data-testid="coupon-applied" className="flex items-center justify-between bg-white rounded-2xl px-3 py-2.5 border border-[#fde0e8]">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 grid place-items-center">
                        <Check className="w-3.5 h-3.5"/>
                      </span>
                      <div>
                        <div className="font-bold text-xs uppercase tracking-wider">{coupon.code}</div>
                        <div className="text-[10px] text-[var(--ink-soft)]">−{formatINR(coupon.discount)}</div>
                      </div>
                    </div>
                    <button type="button" onClick={removeCoupon} data-testid="remove-coupon" className="text-[var(--ink-soft)] hover:text-[var(--bloom)]">
                      <X className="w-4 h-4"/>
                    </button>
                  </div>
                )}
              </div>

              <div className="border-t border-[#fde0e8] mt-5 pt-4 space-y-2 text-sm">
                <Row label="Subtotal" value={formatINR(subtotal)}/>
                {discount > 0 && <Row label={`Discount (${coupon.code})`} value={`−${formatINR(discount)}`} highlight/>}
                <Row label="Shipping" value={shipping === 0 ? "Free" : formatINR(shipping)}/>
                {codCharge > 0 && <Row label="COD handling" value={`+${formatINR(codCharge)}`} />}
                <div className="border-t border-[#fde0e8] pt-2 flex justify-between font-serif font-semibold text-[1.25rem]">
                  <span>Total</span><span data-testid="order-total">{formatINR(total)}</span>
                </div>
              </div>
              <button
                type="submit"
                disabled={processing || (method === "cod" && needsPhoneOtp)}
                data-testid="pay-now-button"
                className="btn btn-primary w-full justify-center mt-5 disabled:opacity-60"
              >
                {processing ? "Processing…" : method === "cod" ? "Place COD order" : "Pay now"}
              </button>
              <div className="mt-4 flex items-center gap-2 text-xs text-[var(--ink-soft)]">
                <ShieldCheck className="w-4 h-4" style={{color:"var(--bloom)"}}/>
                {method === "cod" ? "Cash paid to courier on delivery" : "Secure checkout · Razorpay"}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function normalizeIndianPhone(v) {
  if (!v) return "";
  const d = String(v).replace(/\D/g, "");
  return d.length === 10 ? "91" + d : d;
}

function MethodCard({ active, onClick, icon, title, subtitle, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left rounded-2xl border-2 p-4 transition ${
        active
          ? "bg-white border-[var(--bloom)] shadow-[0_10px_30px_-15px_rgba(224,53,107,.35)]"
          : "bg-white/60 border-[#fde0e8] hover:border-[var(--bloom)]/60"
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-full grid place-items-center"
          style={{ background: active ? "var(--bloom)" : "var(--rose)", color: active ? "#fff" : "var(--bloom)" }}
        >
          {icon}
        </div>
        <div className="flex-1">
          <div className="font-serif font-semibold leading-tight">{title}</div>
          <div className="text-xs text-[var(--ink-soft)] mt-0.5">{subtitle}</div>
        </div>
        <span
          className={`w-4 h-4 rounded-full border-2 shrink-0 ${active ? "bg-[var(--bloom)] border-[var(--bloom)]" : "border-[#e8bfcd]"}`}
        />
      </div>
    </button>
  );
}

function Field({ label, testid, value, onChange, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-[0.16em] text-[var(--ink-soft)] font-bold">{label}</span>
      <input data-testid={testid} value={value} onChange={onChange}
        className="mt-1.5 w-full bg-white border border-[#fde0e8] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--bloom)]"/>
    </label>
  );
}
function Row({ label, value, highlight }) {
  return (
    <div className={`flex justify-between ${highlight ? "text-green-700 font-bold" : "text-[var(--ink-soft)]"}`}>
      <span>{label}</span><span className="font-bold">{value}</span>
    </div>
  );
}
